import * as React from 'react';

import {ModelContext} from '../../../infrastructure/compatibility/ModelContext';
import {Fade} from '../../../utils/Fade';
import {FullscreenGalleryArrows} from './arrows';
import {styled, useTheme} from '@volkswagen-onehub/components-core';

export * from './close-button';

const StyledContent = styled.div`
    display: inline-flex;
    flex-flow: column;
    text-align: center;
    pointer-events: auto;
    outline: none;
`;

const StyledCloseButtonWrapper = styled.div`
    position: absolute;
    z-index: 1;
    top: 0;
    ${props => (props.theme.direction === 'rtl' ? 'left' : 'right')}: 0;
`;

const StyledImageWrapper = styled.div`
    img {
        // scaling
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: calc(100vh - 2 * var(--inset-position));

        // theme
        overflow: hidden;
        border: solid 1px rgba(0, 0, 0, 0.3);
        border-radius: ${props => props.theme.size.static100};
        outline: solid 3px #000;
    }
`;

export interface FullscreenGalleryLayerContentProps {
    children: JSX.Element[];
    index: number;
    closeButton: JSX.Element;
    contextData: {value?: string};
    prevAriaLabel?: string;
    nextAriaLabel?: string;
    dialogAriaLabel?: string;
    onClickImage?: (currentIdx: number, nextIdx: number) => void;
    onClickNext?: (currentIdx: number, nextIdx: number) => void;
    onClickPrevious?: (currentIdx: number, nextIdx: number) => void;
}

export const FullscreenGalleryLayerContent: React.FunctionComponent<
    FullscreenGalleryLayerContentProps
> = props => {
    const {
        children,
        closeButton,
        index,
        contextData,
        prevAriaLabel,
        nextAriaLabel,
        dialogAriaLabel,
        onClickImage = () => {
            return;
        },
        onClickPrevious = () => {
            return;
        },
        onClickNext = () => {
            return;
        }
    } = props;

    const [activeIndex, setActiveIndex] = React.useState(index);
    const {size} = useTheme();
    const contentRef = React.useRef<null | HTMLDivElement>(null);
    const indexNumber = children.length - 1;

    const clickImage = () => {
        const currentIndex = activeIndex;
        setActiveIndex(activeIndex === indexNumber ? 0 : currentIndex + 1);
        onClickImage(currentIndex, currentIndex + 1);
    };

    const clickNext = () => {
        const currentIndex = activeIndex;
        setActiveIndex(activeIndex === indexNumber ? 0 : currentIndex + 1);
        onClickNext(currentIndex, currentIndex + 1);
    };

    const clickPrevious = () => {
        const currentIndex = activeIndex;
        setActiveIndex(activeIndex === 0 ? indexNumber : currentIndex - 1);
        onClickPrevious(currentIndex, currentIndex - 1);
    };

    type KeyHandlerMap = {
        [key: string]: () => void;
    };

    const createKeyHandler = () => {
        const keyHandlerMap: KeyHandlerMap = {
            ArrowLeft: clickPrevious,
            ArrowRight: clickNext
        };
        return (event: React.KeyboardEvent) => {
            const handler = keyHandlerMap[event.key];
            if (handler) {
                handler();
            }
        };
    };

    React.useEffect(() => {
        contentRef.current?.focus();
    }, [index]);

    return (
        <ModelContext.Provider value={contextData}>
            <StyledContent
                ref={contentRef}
                tabIndex={-1}
                onKeyDown={createKeyHandler()}
                role="dialog"
                aria-modal="true"
                aria-label={dialogAriaLabel}
            >
                <StyledCloseButtonWrapper>
                    {closeButton}
                </StyledCloseButtonWrapper>
                <FullscreenGalleryArrows
                    triggerPrevious={clickPrevious}
                    triggerNext={clickNext}
                    prevAriaLabel={prevAriaLabel}
                    nextAriaLabel={nextAriaLabel}
                />
                <StyledImageWrapper onClick={() => clickImage()}>
                    <Fade
                        key={activeIndex}
                        duration={250}
                        directionFrom="right"
                        distance={size.static250}
                        easing="ease-in-out"
                    >
                        {children[activeIndex]}
                    </Fade>
                </StyledImageWrapper>
            </StyledContent>
        </ModelContext.Provider>
    );
};
